import { useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { Plus, Pencil, Trash2, BookOpen, Eye, EyeOff } from "lucide-react";
import { format } from "date-fns";

const KB_CATEGORIES = [
  { value: "company", label: "Company" },
  { value: "pep-exam", label: "PEP Exam" },
  { value: "curriculum", label: "Curriculum" },
  { value: "platform", label: "Platform" },
  { value: "mechanics", label: "Mechanics" },
  { value: "prizes", label: "Prizes" },
  { value: "reports", label: "Reports" },
  { value: "pricing", label: "Pricing" },
  { value: "parents", label: "Parents" },
  { value: "faq", label: "FAQ" },
];

interface KbArticle {
  id: number;
  title: string;
  category: string;
  content: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

interface FormState {
  title: string;
  category: string;
  content: string;
  isActive: boolean;
}

const EMPTY_FORM: FormState = {
  title: "",
  category: "platform",
  content: "",
  isActive: true,
};

export default function AdminKnowledgeBase() {
  const { toast } = useToast();
  const [categoryFilter, setCategoryFilter] = useState<string>("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingArticle, setEditingArticle] = useState<KbArticle | null>(null);
  const [deleteArticleId, setDeleteArticleId] = useState<number | null>(null);
  const [previewContent, setPreviewContent] = useState(false);
  const [form, setForm] = useState<FormState>(EMPTY_FORM);

  const queryParams = new URLSearchParams();
  if (categoryFilter && categoryFilter !== "all") queryParams.set("category", categoryFilter);
  if (searchQuery.trim()) queryParams.set("q", searchQuery.trim());
  const queryString = queryParams.toString();

  const { data: articles = [], isLoading } = useQuery<KbArticle[]>({
    queryKey: ["/api/admin/kb", categoryFilter, searchQuery],
    queryFn: async () => {
      const url = `/api/admin/kb${queryString ? `?${queryString}` : ""}`;
      const res = await fetch(url, { credentials: "include" });
      if (!res.ok) throw new Error("Failed to load articles");
      return res.json();
    },
  });

  const createMutation = useMutation({
    mutationFn: async (data: FormState) => {
      return await apiRequest("POST", "/api/admin/kb", data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/kb"] });
      toast({ title: "Article created successfully" });
      closeDialog();
    },
    onError: (error: any) => {
      toast({ title: "Error", description: error.message || "Failed to create article", variant: "destructive" });
    },
  });

  const updateMutation = useMutation({
    mutationFn: async ({ id, data }: { id: number; data: Partial<FormState> }) => {
      return await apiRequest("PATCH", `/api/admin/kb/${id}`, data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/kb"] });
      toast({ title: "Article updated successfully" });
      closeDialog();
    },
    onError: (error: any) => {
      toast({ title: "Error", description: error.message || "Failed to update article", variant: "destructive" });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: number) => {
      return await apiRequest("DELETE", `/api/admin/kb/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/kb"] });
      toast({ title: "Article deleted successfully" });
      setDeleteArticleId(null);
    },
    onError: (error: any) => {
      toast({ title: "Error", description: error.message || "Failed to delete article", variant: "destructive" });
    },
  });

  function closeDialog() {
    setDialogOpen(false);
    setEditingArticle(null);
    setForm(EMPTY_FORM);
    setPreviewContent(false);
  }

  function openCreate() {
    setEditingArticle(null);
    setForm(EMPTY_FORM);
    setDialogOpen(true);
  }

  function openEdit(article: KbArticle) {
    setEditingArticle(article);
    setForm({
      title: article.title,
      category: article.category,
      content: article.content,
      isActive: article.isActive,
    });
    setDialogOpen(true);
  }

  function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!form.title.trim()) {
      toast({ title: "Validation error", description: "Title is required", variant: "destructive" });
      return;
    }
    if (!form.content.trim()) {
      toast({ title: "Validation error", description: "Content is required", variant: "destructive" });
      return;
    }
    if (editingArticle) {
      updateMutation.mutate({ id: editingArticle.id, data: form });
    } else {
      createMutation.mutate(form);
    }
  }

  function getCategoryLabel(value: string) {
    return KB_CATEGORIES.find(c => c.value === value)?.label ?? value;
  }

  if (isLoading) {
    return (
      <div className="p-6 space-y-6">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <h1 className="text-2xl font-bold">Knowledge Base</h1>
        </div>
        <div className="text-muted-foreground">Loading articles...</div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h1 className="text-2xl font-bold">Knowledge Base</h1>
          <p className="text-muted-foreground text-sm">Manage articles that power Nya's WhatsApp responses</p>
        </div>
        <Button onClick={openCreate}>
          <Plus className="mr-2 h-4 w-4" />
          New Article
        </Button>
      </div>

      <div className="flex flex-wrap gap-3">
        <Input
          placeholder="Search by title..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="max-w-xs"
        />
        <Select value={categoryFilter} onValueChange={setCategoryFilter}>
          <SelectTrigger className="w-44">
            <SelectValue placeholder="All categories" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All categories</SelectItem>
            {KB_CATEGORIES.map(cat => (
              <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {articles.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <BookOpen className="mx-auto h-12 w-12 text-muted-foreground mb-4" />
            <p className="text-muted-foreground">No articles found. Create your first KB article.</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {articles.map((article) => (
            <Card key={article.id}>
              <CardContent className="py-4 px-5">
                <div className="flex items-start justify-between gap-4 flex-wrap">
                  <div className="flex-1 min-w-0 space-y-1.5">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-medium text-sm">{article.title}</span>
                      <Badge variant="outline" className="text-xs">{getCategoryLabel(article.category)}</Badge>
                      <Badge variant={article.isActive ? "default" : "secondary"} className="text-xs">
                        {article.isActive ? "Active" : "Inactive"}
                      </Badge>
                    </div>
                    <p className="text-xs text-muted-foreground line-clamp-2">{article.content}</p>
                    <p className="text-xs text-muted-foreground">
                      Updated: {format(new Date(article.updatedAt), "MMM d, yyyy HH:mm")}
                    </p>
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    <Button size="icon" variant="ghost" onClick={() => openEdit(article)}>
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <Button
                      size="icon"
                      variant="ghost"
                      onClick={() => setDeleteArticleId(article.id)}
                      disabled={deleteMutation.isPending}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <AlertDialog open={deleteArticleId !== null} onOpenChange={(open) => { if (!open) setDeleteArticleId(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Article</AlertDialogTitle>
            <AlertDialogDescription>This will permanently remove the article from the knowledge base. This action cannot be undone.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => { if (deleteArticleId !== null) deleteMutation.mutate(deleteArticleId); }}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Dialog open={dialogOpen} onOpenChange={(open) => { if (!open) closeDialog(); }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingArticle ? "Edit Article" : "New Article"}</DialogTitle>
            <DialogDescription>
              {editingArticle ? "Update the knowledge base article." : "Create a new knowledge base article for Nya to reference."}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={onSubmit} className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="kb-title">Title</Label>
              <Input
                id="kb-title"
                placeholder="e.g. PEP Exam and CBT Explained"
                value={form.title}
                onChange={(e) => setForm(f => ({ ...f, title: e.target.value }))}
              />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="kb-category">Category</Label>
              <Select value={form.category} onValueChange={(val) => setForm(f => ({ ...f, category: val }))}>
                <SelectTrigger id="kb-category">
                  <SelectValue placeholder="Select category" />
                </SelectTrigger>
                <SelectContent>
                  {KB_CATEGORIES.map(cat => (
                    <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-1.5">
              <div className="flex items-center justify-between">
                <Label htmlFor="kb-content">Content</Label>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => setPreviewContent(p => !p)}
                  className="h-7 text-xs"
                >
                  {previewContent ? <><EyeOff className="h-3 w-3 mr-1" /> Edit</> : <><Eye className="h-3 w-3 mr-1" /> Preview</>}
                </Button>
              </div>
              {previewContent ? (
                <div className="min-h-[200px] rounded-md border p-3 text-sm whitespace-pre-wrap bg-muted/30">
                  {form.content || <span className="text-muted-foreground">(empty)</span>}
                </div>
              ) : (
                <Textarea
                  id="kb-content"
                  placeholder="Write the article content here..."
                  value={form.content}
                  onChange={(e) => setForm(f => ({ ...f, content: e.target.value }))}
                  rows={10}
                  className="font-mono text-sm"
                />
              )}
            </div>

            <div className="flex items-center justify-between rounded-lg border p-3">
              <Label htmlFor="kb-active" className="mb-0 cursor-pointer">Active (visible to Nya)</Label>
              <Switch
                id="kb-active"
                checked={form.isActive}
                onCheckedChange={(checked) => setForm(f => ({ ...f, isActive: checked }))}
              />
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={closeDialog}>
                Cancel
              </Button>
              <Button type="submit" disabled={createMutation.isPending || updateMutation.isPending}>
                {editingArticle ? "Update" : "Create"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
